import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { useLocation } from 'react-router-dom';
import withSizes from 'react-sizes';
import { Link } from '@sunrun/component-library';
import { HamburgerMenu } from '@sunrun/component-library/icons';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { Color } from '@sunrun/mui-theme';
import mixpanel from '../utilities/mixpanel';
import { DASHBOARD_ROUTE } from '../constants/routes';
import mapSizesToProps from '../components/general/utilities/mapSizesToProps';
import { toggleSideDrawer } from '../redux/modules/app';
import { useFriendlyName, useOpportunity, useIsEVChargerProspect, useIsPVHasEV } from '../hooks/useUser';
import { MIN_DESKTOP_BREAKPOINT, MAX_DESKTOP_BREAKPOINT } from '../components/general/constants/_breakpoints';
import SystemNotification from '../components/general/SystemNotification';
import MySunrunLogo from '../images/MySunrunLogo';
import { ALERT_DOT_COLOR, PRODUCT_BACKGROUND } from '../components/general/constants/_colors';
import { HEADER_HEIGHT } from '../components/general/constants/_sizes';
import { ChatControlButton } from '../components/support/ChatButton';

const StyledHeader = styled.div`
  display: ${props => (props.pathname === DASHBOARD_ROUTE ? 'block' : 'none')};

  @media only screen and (max-width: ${MAX_DESKTOP_BREAKPOINT}) {
    justify-content: space-between;
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    z-index: 5;
  }

  @media only screen and (min-width: ${MIN_DESKTOP_BREAKPOINT}) {
    display: block;
    min-height: ${HEADER_HEIGHT};
  }
`;

const StyledAppBar = styled(AppBar)`
  background-color: ${PRODUCT_BACKGROUND} !important;
  color: ${Color.BLACK_85} !important;
  min-height: ${HEADER_HEIGHT} !important;
  box-shadow: none !important;
  padding-top: env(safe-area-inset-top);
  margin-bottom: env(safe-area-inset-top);
`;

const StyledToolbar = styled(Toolbar)`
  min-height: ${HEADER_HEIGHT} !important;
  display: flex;
  justify-content: space-between;

  // Exists here for specificity
  .greeting-link:hover,
  .greeting-link:focus {
    background-color: transparent;
  }
`;

const HeaderWrapper = styled.div`
  position: relative;
  width: 100%;
  border-bottom: 1px solid #00000033;
  z-index: 25;
`;

const StyledLink = styled(Link)`
  text-decoration: none !important;
  color: #000000;
`;

const StyledTypography = styled(Typography)`
  font-size: 24px !important;
  display: flex;
  color: ${Color.BLACK_85};
  font-family: Roobert, sans-serif !important;

  > div {
    height: 35px;
  }
`;

const MobileIconWrap = styled.div`
  position: relative;
  display: inline-block;
`;

const Greeting = styled.div`
  white-space: nowrap;
  color: #000000;

  @media only screen and (max-width: ${MAX_DESKTOP_BREAKPOINT}) {
    display: none;
  }
`;

const Logo = styled(MySunrunLogo)`
  position: relative;
  height: 32px;
  top: 2px;
`;

const ButtonPlaceHolder = styled.div`
  min-width: 130px;
`;

const ChargerAlertDot = styled.span`
  border-radius: 50%;
  position: absolute;
  background: ${ALERT_DOT_COLOR};
  width: 12px;
  height: 12px;
  top: 8px;
  right: -5px;
  z-index: 100;
`;

const AppHeader = ({ isMobile }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const friendlyName = useFriendlyName();
  const oppty = useOpportunity();
  const isEVChargerProspect = useIsEVChargerProspect();
  const isPVHasEV = useIsPVHasEV();

  const drawerToggle = () => {
    dispatch(toggleSideDrawer());
    return false;
  };

  const onChatButtonClick = () => {
    mixpanel.track('Open Chat from Header');
  };

  const greetingText = `Hello${friendlyName ? `, ${friendlyName}` : ''}!`;

  return (
    <HeaderWrapper>
      <StyledHeader pathname={pathname}>
        <StyledAppBar position="static">
          <StyledToolbar>
            <StyledLink to={DASHBOARD_ROUTE} className="greeting-link">
              <StyledTypography component="div">
                {isMobile && (
                  <MobileIconWrap onClick={drawerToggle}>
                    <HamburgerMenu width="35px" />
                    {isPVHasEV && <ChargerAlertDot />}
                  </MobileIconWrap>
                )}
                <Greeting>{greetingText}</Greeting>
              </StyledTypography>
            </StyledLink>
            <Logo />
            {!isMobile && <SystemNotification />}
            {oppty && !oppty.isMultiFamilyHome && !isEVChargerProspect && <ChatControlButton fixed={false} onClick={onChatButtonClick} />}
            {(!oppty || oppty.isMultiFamilyHome || isEVChargerProspect) && <ButtonPlaceHolder />}
          </StyledToolbar>
        </StyledAppBar>
      </StyledHeader>
    </HeaderWrapper>
  );
};

AppHeader.propTypes = {
  isMobile: PropTypes.bool.isRequired,
};

export default withSizes(mapSizesToProps)(AppHeader);
